import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { PastasRepository } from '../../../data/repositories/PastasRepository';
import { ProcessRepository } from '../../../data/repositories/ProcessRepository';
import { ProcessValidationRepository } from '../../../data/repositories/ProcessValidationRepository';
import {
  HabitaEmptyState,
  classNames,
  habitaSurfaceClass,
} from '../../../shared/components/habita';
import type {
  HabitaDocumento,
  HabitaPendingItems,
  HabitaProcessDetail,
  HabitaProcessStatusHistoryEntry,
  HabitaProcessStatusTransitionResponse,
  HabitaRequestDocumentAdjustmentResult,
  HabitaValidateFolderResult,
  PastaStatus,
  ProcessFileModel,
} from '../../../shared/models';
import {
  rememberPastaDetailExternalIds,
  rememberPastaDetailNavigation,
  resolvePastaDetailExternalIds,
  resolvePastaDetailNavigation,
  type PastaDetailExternalIds,
} from '../../../shared/navigation/pastaDetailNavigation';
import {
  getAuthenticatedUserEmail,
  getAuthProfiles,
  getUser,
  resolvePreviewMimeType,
} from '../../../shared/utils';
import { PastaDetalheExperience } from './components/PastaDetalheWidgets';

function currentExternalId(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text && text !== '-' ? text : null;
}

function mergeDetailExternalIds(
  detail: HabitaProcessDetail | null,
  externalIds: PastaDetailExternalIds | null
): HabitaProcessDetail | null {
  if (!detail || !externalIds) return detail;

  return {
    ...detail,
    crm_contact_id: currentExternalId(detail.crm_contact_id) ?? externalIds.crmContactId ?? null,
    chatwoot_contact_id: currentExternalId(detail.chatwoot_contact_id)
      ?? externalIds.chatwootContactId
      ?? null,
  };
}

function reviewerEmail(): string {
  return currentExternalId(getAuthenticatedUserEmail(getUser())) ?? 'usuario_autenticado';
}

function normalizeEmail(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

function isSameEmail(left: unknown, right: unknown): boolean {
  const normalizedLeft = normalizeEmail(left);
  const normalizedRight = normalizeEmail(right);
  return Boolean(normalizedLeft && normalizedRight && normalizedLeft === normalizedRight);
}

function canManageFolderValidation(user: unknown, detail: HabitaProcessDetail | null): boolean {
  if (!detail) return false;

  const profiles = getAuthProfiles(user);
  if (profiles.some((profile) => profile === 'ADMIN' || profile === 'SUPERVISOR')) return true;
  if (!profiles.includes('SDR')) return false;

  const userEmail = getAuthenticatedUserEmail(user);
  return [
    detail.responsible_email,
    detail.responsible,
    detail.created_by,
  ].some((candidate) => isSameEmail(candidate, userEmail));
}

function canManagePreAnalysis(user: unknown): boolean {
  return getAuthProfiles(user).some((profile) => (
    profile === 'ADMIN' ||
    profile === 'SUPERVISOR' ||
    profile === 'ANALISTA'
  ));
}

export default function PastaDetalhe() {
  const { id, idProcesso } = useParams();
  const pastaId = id ?? idProcesso;
  const location = useLocation();
  const pastasRepository = useMemo(() => new PastasRepository(), []);
  const processRepository = useMemo(() => new ProcessRepository(), []);
  const processValidationRepository = useMemo(() => new ProcessValidationRepository(), []);
  const currentUser = useMemo(() => getUser(), []);
  const [detail, setDetail] = useState<HabitaProcessDetail | null>(null);
  const [statusHistory, setStatusHistory] = useState<HabitaProcessStatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const loadingRef = useRef(true);
  const navigationExternalIds = useMemo(
    () => resolvePastaDetailExternalIds(pastaId, location.state),
    [pastaId, location.state]
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadPasta = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!pastaId) {
        requestSequenceRef.current += 1;
        setDetail(null);
        setError('Pasta não encontrada.');
        loadingRef.current = false;
        setLoading(false);
        return;
      }

      if (silent && loadingRef.current) return;

      const requestId = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestId;

      try {
        if (!silent) {
          loadingRef.current = true;
          setLoading(true);
        }
        setError(null);
        const fetched = idProcesso
          ? await pastasRepository.fetchPastaByProcessoId(idProcesso)
          : await pastasRepository.fetchPastaById(pastaId);
        const processIdForHistory = fetched?.id ?? pastaId;
        const fetchedStatusHistory = processIdForHistory
          ? await processValidationRepository
              .fetchStatusHistory(processIdForHistory)
              .then((result) => result.history)
              .catch((historyError) => {
                console.warn('Erro ao carregar histórico de status da pasta:', historyError);
                return [];
              })
          : [];
        if (!mountedRef.current || requestSequenceRef.current !== requestId) return;
        const merged = mergeDetailExternalIds(fetched, navigationExternalIds);
        setDetail(merged);
        setStatusHistory(fetchedStatusHistory);
        setError(merged ? null : 'Pasta não encontrada.');
      } catch (loadError) {
        if (!mountedRef.current || requestSequenceRef.current !== requestId) return;
        console.error('Erro ao carregar pasta:', loadError);
        setError('Não foi possível carregar os dados da pasta.');
        if (!silent) {
          setDetail(null);
          setStatusHistory([]);
        }
      } finally {
        if (!silent && mountedRef.current && requestSequenceRef.current === requestId) {
          loadingRef.current = false;
          setLoading(false);
        }
      }
    },
    [currentUser, idProcesso, navigationExternalIds, pastaId, pastasRepository, processValidationRepository]
  );

  useEffect(() => {
    void loadPasta();
  }, [loadPasta]);

  const resolvedNavigation = useMemo(
    () => resolvePastaDetailNavigation(pastaId, location.state),
    [pastaId, location.state]
  );
  const backTo = resolvedNavigation.target.from;
  const backLabel = resolvedNavigation.target.label;
  const loadDocumentPreview = useCallback(async (documento: HabitaDocumento) => {
    const numericId = Number(documento.id);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      throw new Error('Documento sem identificador numérico para download.');
    }

    const result = await processRepository.downloadProcessFile({
      ...(documento.file ?? {}),
      id: numericId,
      name: documento.arquivoNomeOriginal ?? documento.nome,
      path: documento.path ?? '',
      ai_status: '',
      doc_type: documento.file?.doc_type ?? documento.categoria,
      process_id: documento.processId ? Number(documento.processId) : null,
    } satisfies ProcessFileModel);

    return {
      data: result.data,
      mimeType: resolvePreviewMimeType({
        data: result.data,
        mimeType: result.mimeType,
        names: [documento.arquivoNomeOriginal, documento.path, documento.nome],
      }),
    };
  }, [processRepository]);

  const loadProcessFiles = useCallback((processId: string) => (
    processRepository.getProcessFiles(processId)
  ), [processRepository]);

  const loadPendingItems = useCallback((processId: string): Promise<HabitaPendingItems> => (
    processValidationRepository.fetchPendingItems(processId)
  ), [processValidationRepository]);

  const requestDocumentAdjustment = useCallback(async ({
    fileIds,
    processId,
    rejectionReason,
  }: {
    fileIds: number[];
    processId: string;
    rejectionReason: string;
  }): Promise<HabitaRequestDocumentAdjustmentResult> => {
    const result = await processValidationRepository.requestDocumentAdjustment(processId, {
      reviewedBy: reviewerEmail(),
      files: fileIds.map((processFileId) => ({
        processFileId,
        rejectionReason,
      })),
    });

    await loadPasta({ silent: true });

    return result;
  }, [loadPasta, processValidationRepository]);

  const validateFolder = useCallback(async (processId: string): Promise<HabitaValidateFolderResult> => {
    const result = await processValidationRepository.validateFolder(processId, {
      reviewedBy: reviewerEmail(),
    });

    await loadPasta({ silent: true });

    return result;
  }, [loadPasta, processValidationRepository]);

  const registerPreAnalysisResult = useCallback(async ({
    motive,
    processId,
    toStatus,
  }: {
    motive?: string;
    processId: string;
    toStatus: PastaStatus;
  }): Promise<HabitaProcessStatusTransitionResponse> => {
    const result = await processValidationRepository.transitionStatus(processId, {
      changed_by: reviewerEmail(),
      motive,
      to_status: toStatus,
    });

    await loadPasta({ silent: true });

    return result;
  }, [loadPasta, processValidationRepository]);

  const canManagePreAnalysisActions = useMemo(
    () => canManagePreAnalysis(currentUser),
    [currentUser]
  );
  const canValidateFolder = useMemo(
    () => canManageFolderValidation(currentUser, detail),
    [currentUser, detail]
  );

  useEffect(() => {
    if (resolvedNavigation.source === 'state') {
      rememberPastaDetailNavigation(pastaId, resolvedNavigation.target);
    }
  }, [pastaId, resolvedNavigation]);

  useEffect(() => {
    if (navigationExternalIds) {
      rememberPastaDetailExternalIds(pastaId, navigationExternalIds);
    }
  }, [navigationExternalIds, pastaId]);

  if (loading) {
    return (
      <section className="w-full space-y-4">
        <div className="h-10 w-80 animate-pulse rounded bg-muted" />
        <div className="grid grid-cols-12 gap-4">
          <div className={classNames(habitaSurfaceClass, 'col-span-12 h-[560px] animate-pulse xl:col-span-4')} />
          <div className={classNames(habitaSurfaceClass, 'col-span-12 h-[560px] animate-pulse xl:col-span-8')} />
        </div>
      </section>
    );
  }

  if (!detail || error) {
    return (
      <section className="w-full space-y-4">
        <Link
          to={backTo}
          className="inline-flex h-9 items-center gap-2 rounded-md px-2 font-instrument text-sm text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary"
        >
          <i className="ri-arrow-left-line text-lg" aria-hidden />
          Voltar para {backLabel}
        </Link>
        <HabitaEmptyState
          icon="ri-folder-warning-line"
          title={error ?? 'Pasta não encontrada.'}
          description="Confira se o identificador da pasta está correto."
        />
      </section>
    );
  }

  return (
    <PastaDetalheExperience
      detail={detail}
      backTo={backTo}
      backLabel={backLabel}
      canManagePreAnalysisActions={canManagePreAnalysisActions}
      canValidateFolder={canValidateFolder}
      onLoadDocumentPreview={loadDocumentPreview}
      onLoadPendingItems={loadPendingItems}
      onLoadProcessFiles={loadProcessFiles}
      onRegisterPreAnalysisResult={registerPreAnalysisResult}
      onRequestDocumentAdjustment={requestDocumentAdjustment}
      onValidateFolder={validateFolder}
      statusHistory={statusHistory}
      validationReviewerLabel={reviewerEmail()}
    />
  );
}
